type ReportFunction = (data: { duration: number; interactive: number }) => void

interface PageStayTrackerOptions {
  reportInterval?: number
  reportFunction?: ReportFunction
}

class PageStayTracker {
  private reportInterval: number
  private reportFunction: ReportFunction | null
  private duration: number
  private timer: number | null
  private interactive: number
  private startTime: number
  private visibilityListener: (() => void) | null
  private destroyed: boolean

  constructor({
    reportInterval = 10000,
    reportFunction = null,
  }: PageStayTrackerOptions) {
    this.reportInterval = reportInterval
    this.reportFunction = reportFunction
    this.duration = 0
    this.timer = null
    this.interactive = 0
    this.startTime = Date.now()
    this.visibilityListener = null
    this.destroyed = false

    // 绑定事件处理程序
    this.handleUserInteraction = this.handleUserInteraction.bind(this)
    this.startReporting = this.startReporting.bind(this)
    this.stopReporting = this.stopReporting.bind(this)
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this)
    this.handleBeforeUnload = this.handleBeforeUnload.bind(this)

    // 添加页面可见性变化事件监听器
    document.addEventListener('visibilitychange', this.handleVisibilityChange)

    // 添加页面关闭前事件监听器 、关闭浏览器也会触发 visibilitychange 事件，所以不需要 beforeunload
    // window.addEventListener('beforeunload', this.handleBeforeUnload)
  }

  // 用户交互事件处理程序
  private handleUserInteraction() {
    this.interactive = 1
  }

  // 开始上报
  public startReporting() {
    if (this.destroyed || this.timer) return
    // 重置初始值
    this.startTime = Date.now()
    this.interactive = 0

    // 添加用户交互事件监听
    window.addEventListener('mousemove', this.handleUserInteraction)
    window.addEventListener('mousedown', this.handleUserInteraction)
    window.addEventListener('mouseup', this.handleUserInteraction)
    window.addEventListener('wheel', this.handleUserInteraction)

    // 设置上报定时器
    this.timer = window.setInterval(() => {
      this.report()
    }, this.reportInterval)
  }

  // 停止上报
  public stopReporting() {
    if (this.timer !== null) {
      clearInterval(this.timer)
      this.timer = null
    }
    this.report() // 立即上报当前停留时间

    // 移除用户交互事件监听
    window.removeEventListener('mousemove', this.handleUserInteraction)
    window.removeEventListener('mousedown', this.handleUserInteraction)
    window.removeEventListener('mouseup', this.handleUserInteraction)
    window.removeEventListener('wheel', this.handleUserInteraction)
  }

  // 销毁实例
  public destroy() {
    this.destroyed = true
    this.stopReporting()
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange,
    )
    // window.removeEventListener('beforeunload', this.handleBeforeUnload)
  }

  // 处理页面可见性变化
  private handleVisibilityChange() {
    if (document.hidden) {
      // 页面隐藏时停止计时器
      this.stopReporting()
    } else {
      // 页面显示时重新启动计时器
      this.startReporting()
    }
  }

  // 处理页面关闭前事件
  private handleBeforeUnload(event: BeforeUnloadEvent) {
    // 停止计时器并立即上报数据
    this.stopReporting()

    // 可选：在某些浏览器中，需要返回一个字符串以触发确认框
    event.returnValue = '' // 兼容旧版浏览器
  }

  // 上报数据
  private report() {
    const currentTime = Date.now()
    this.duration = Math.round(currentTime - this.startTime) // 转换为秒

    // 调用传入的上报函数
    if (this.reportFunction) {
      this.reportFunction({
        duration: this.duration,
        interactive: this.interactive,
      })
    }

    // 重置上报参数
    this.startTime = currentTime
    this.interactive = 0
  }
}

// 模块导出
export default PageStayTracker
